
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Download, 
  FileText, 
  Video, 
  File,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  Mail
} from 'lucide-react';
import KitNavigation from './KitNavigation';

interface KitFile {
  name: string;
  type: 'pdf' | 'docx' | 'mp4' | 'link';
  size?: string;
  placeholder?: boolean;
}

interface KitSection {
  title: string;
  description: string;
  files: KitFile[];
}

interface KitTemplateProps {
  kitId: string;
  kitTitle: string;
  kitDescription: string;
  icon: React.ReactNode;
  sections: {
    downloads: KitSection;
    sops: KitSection;
    training: KitSection;
  };
}

const KitTemplate = ({ kitId, kitTitle, kitDescription, icon, sections }: KitTemplateProps) => {
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf': return <FileText className="h-4 w-4 text-red-400" />;
      case 'docx': return <File className="h-4 w-4 text-blue-400" />;
      case 'mp4': return <Video className="h-4 w-4 text-purple-400" />;
      case 'link': return <ExternalLink className="h-4 w-4 text-emerald-400" />;
      default: return <File className="h-4 w-4 text-slate-400" />;
    }
  };

  const getFileTypeLabel = (type: string) => {
    switch (type) {
      case 'pdf': return 'PDF';
      case 'docx': return 'DOC';
      case 'mp4': return 'Video';
      case 'link': return 'Link';
      default: return 'File';
    }
  };

  const getSupportInfo = () => {
    switch (kitId) {
      case 'launch':
        return {
          email: 'ops@juvmedspa.com',
          secondary: 'Slack: #studio-launch-support'
        };
      case 'training':
        return {
          email: 'alma@juvmedspa.com',
          secondary: 'Chat: /hq/agents/alma'
        };
      case 'marketing':
        return {
          email: 'marketing@juvmedspa.com',
          secondary: 'Resources: /hq/kits/marketing/assets'
        };
      case 'compliance':
        return {
          email: 'legal@juvmedspa.com',
          secondary: 'Portal: /hq/compliance-dashboard'
        };
      case 'technology':
        return {
          email: 'tech@juvmedspa.com',
          secondary: 'Live Support: /hq/tech-support'
        };
      default:
        return {
          email: 'support@juvmedspa.com',
          secondary: 'Contact HQ Support'
        };
    }
  };

  const renderSection = (section: KitSection, sectionKey: string) => {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
      <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-700/30 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center space-x-2">
                    <span>{section.title}</span>
                  </CardTitle>
                  <p className="text-slate-300 text-sm mt-1">{section.description}</p>
                </div>
                <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <div className="space-y-3">
                {section.files.map((file, index) => (
                  <div 
                    key={index} 
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      file.placeholder 
                        ? 'bg-slate-700/20 border-slate-600/30' 
                        : 'bg-slate-700/40 border-slate-500/30 hover:bg-slate-700/60'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {getFileIcon(file.type)}
                      <div>
                        <p className={`font-medium ${file.placeholder ? 'text-slate-400' : 'text-white'}`}>
                          {file.name}
                        </p>
                        {file.size && (
                          <p className="text-slate-400 text-xs">{file.size}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className="bg-slate-600/50 text-slate-300 border-slate-500/50 text-xs">
                        {getFileTypeLabel(file.type)}
                      </Badge>
                      {!file.placeholder && (
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  const supportInfo = getSupportInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                {icon}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">{kitTitle}</h1>
                <p className="mt-1 text-amber-200">{kitDescription}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kit Navigation */}
      <KitNavigation currentKit={kitId} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Downloads Section */}
          {renderSection(sections.downloads, 'downloads')}

          {/* SOPs Section */}
          {renderSection(sections.sops, 'sops')}

          {/* Training Section */}
          {renderSection(sections.training, 'training')}

          {/* Support Section */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-emerald-400" />
                <span>Support</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-slate-300">
                  Need help with this kit? Our support team and AI assistants are here to help.
                </p>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-2 text-slate-300">
                    <Mail className="h-4 w-4" />
                    <span>Email: {supportInfo.email}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-300">
                    <MessageSquare className="h-4 w-4" />
                    <span>{supportInfo.secondary}</span>
                  </div>
                </div>
                <div className="flex space-x-4 pt-2">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Contact HQ Support
                  </Button>
                  <Button variant="outline" className="border-slate-600 text-slate-300">
                    Schedule with Alma
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default KitTemplate;
